import { AnalogGatewayPort } from '@reliance/types/models/AnalogGatewayPort';
import { FacilityAnalogGateway } from '@reliance/types/models/FacilityAnalogGateway';
import { FacilityNumber } from '@reliance/types/models/FacilityNumber';
import { Station } from '@reliance/types/models/Station';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import api from '../common/api';
import { IStatusResponse } from '../common/reliance-api-service';
import { formatPhoneNumber } from '../format/PhoneNumber';
import { useFacilityAnalogGatewayModalLookups } from '../hooks/FacilityAnalogGateway';

type AddEditModalProps = {
  facility: { id?: number } | null | undefined;
  show: boolean;
  editGateway: FacilityAnalogGateway | null | undefined;
  onClose(saved: boolean): void;
};

function gatewayDraftFromEdit(
  edit: FacilityAnalogGateway | null | undefined,
  facilityId: number
): FacilityAnalogGateway {
  if (edit == null) {
    return {
      name: '',
      model: '',
      ipAddress: '',
      sipAuthUsername: null,
      sipAuthPassword: null,
      analogGatewayPorts: [],
      facilityId,
    };
  }
  const { fxoPorts: _fxo, fxsPorts: _fxs, ...rest } = edit;
  const cloned = JSON.parse(JSON.stringify(rest)) as FacilityAnalogGateway;
  cloned.analogGatewayPorts = (cloned.analogGatewayPorts ?? []).map((p) => ({
    ...p,
    stationId: p.station?.id ?? p.stationId ?? null,
    facilityNumberId: p.facilityNumber?.id ?? p.facilityNumberId ?? null,
  }));
  return cloned;
}

type PortRowProps = {
  port: AnalogGatewayPort;
  portIndex: number;
  stations: Station[];
  facilityNumbers: FacilityNumber[];
  updatePort: (index: number, patch: Partial<AnalogGatewayPort>) => void;
  removePort: (index: number) => void;
  togglePortStatus: (index: number) => void;
};

const AnalogGatewayPortRow: React.FC<PortRowProps> = ({
  port,
  portIndex,
  stations,
  facilityNumbers,
  updatePort,
  removePort,
  togglePortStatus,
}) => {
  const stationsWithId = useMemo(
    () => stations.filter((s): s is Station & { id: number } => s.id != null),
    [stations]
  );

  return (
    <Row className="mb-2">
      <Col md={2}>
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control
            required
            value={port.name}
            onChange={(e) => updatePort(portIndex, { name: e.target.value })}
          />
        </Form.Group>
      </Col>
      <Col md={1}>
        <Form.Group>
          <Form.Label>Port</Form.Label>
          <Form.Control
            required
            type="number"
            value={port.hardwarePort}
            onChange={(e) =>
              updatePort(portIndex, { hardwarePort: Number(e.target.value) })
            }
          />
        </Form.Group>
      </Col>
      {port.type === 'FXO' && (
        <Col md={3}>
          <Form.Group>
            <Form.Label>Facility Number</Form.Label>
            <Form.Select
              value={port.facilityNumberId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                const id = v === '' ? null : Number(v);
                const fn =
                  id == null ? undefined : facilityNumbers.find((n) => n.id === id);
                updatePort(portIndex, {
                  facilityNumberId: id,
                  facilityNumber: fn,
                });
              }}>
              <option value="">—</option>
              {facilityNumbers.map((n) => (
                <option key={n.id} value={n.id}>
                  {formatPhoneNumber(n.ani)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      )}
      {port.type === 'FXS' && (
        <Col md={3}>
          <Form.Group>
            <Form.Label>Phone</Form.Label>
            <Form.Select
              value={port.station?.id ?? port.stationId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                const id = v === '' ? null : Number(v);
                const st =
                  id == null ? undefined : stationsWithId.find((s) => s.id === id);
                updatePort(portIndex, {
                  stationId: id,
                  station: st,
                });
              }}>
              <option value="">—</option>
              {stationsWithId.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      )}
      <Col md={port.type === 'FXO' || port.type === 'FXS' ? 5 : 9}>
        <Form.Group>
          <Form.Label className="d-block">&nbsp;</Form.Label>
          <Button
            type="button"
            variant={port.status === 'Active' ? 'success' : 'danger'}
            className="me-1"
            onClick={() => togglePortStatus(portIndex)}>
            {port.status}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => removePort(portIndex)}
            aria-label="Remove port">
            <i className="fas fa-trash-alt" />
          </Button>
        </Form.Group>
      </Col>
    </Row>
  );
};

const AddEditModal: React.FC<AddEditModalProps> = ({
  facility,
  show,
  editGateway,
  onClose,
}) => {
  const facilityId = facility?.id;
  const modalOpen = Boolean(show && facilityId != null);

  const [gateway, setGateway] = useState<FacilityAnalogGateway>(() =>
    gatewayDraftFromEdit(undefined, facilityId ?? 0)
  );
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookups = useFacilityAnalogGatewayModalLookups(facilityId ?? -1, modalOpen);

  useEffect(() => {
    if (!modalOpen || facilityId == null) {
      return;
    }
    setIsEditing(editGateway?.id != null);
    setGateway(gatewayDraftFromEdit(editGateway, facilityId));
    setError(null);
    setSaving(false);
  }, [modalOpen, editGateway, facilityId]);

  useEffect(() => {
    if (lookups.error != null) {
      setError('Unable to load phones or facility numbers');
    }
  }, [lookups.error]);

  const fxoPorts = useMemo(
    () => gateway.analogGatewayPorts.filter((p) => p.type === 'FXO'),
    [gateway.analogGatewayPorts]
  );
  const fxsPorts = useMemo(
    () => gateway.analogGatewayPorts.filter((p) => p.type === 'FXS'),
    [gateway.analogGatewayPorts]
  );

  const updatePort = useCallback((index: number, patch: Partial<AnalogGatewayPort>) => {
    setGateway((g) => {
      const ports = [...g.analogGatewayPorts];
      ports[index] = { ...ports[index], ...patch };
      return { ...g, analogGatewayPorts: ports };
    });
  }, []);

  const addPort = useCallback((type: string) => {
    setGateway((g) => {
      const maximumPort = g.analogGatewayPorts
        .filter((p) => String(p.type) === type)
        .reduce((prev, cur) => (prev > cur.hardwarePort ? prev : cur.hardwarePort), -1);
      const next = maximumPort + 1;
      const nextPort: AnalogGatewayPort = {
        name: `${type}${(`0${next}`).slice(-2)}`,
        type,
        hardwarePort: next,
        status: 'Active',
      };
      return { ...g, analogGatewayPorts: [...g.analogGatewayPorts, nextPort] };
    });
  }, []);

  const togglePortStatus = useCallback((index: number) => {
    setGateway((g) => {
      const ports = [...g.analogGatewayPorts];
      const p = ports[index];
      ports[index] = {
        ...p,
        status: p.status === 'Active' ? 'Inactive' : 'Active',
      };
      return { ...g, analogGatewayPorts: ports };
    });
  }, []);

  const removePort = useCallback((index: number) => {
    setGateway((g) => ({
      ...g,
      analogGatewayPorts: g.analogGatewayPorts.filter((_, i) => i !== index),
    }));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const data: FacilityAnalogGateway = {
      ...gateway,
      analogGatewayPorts: gateway.analogGatewayPorts.map((p) => ({
        ...p,
        stationId: p.station?.id ?? p.stationId ?? null,
        facilityNumberId: p.facilityNumber?.id ?? p.facilityNumberId ?? null,
      })),
    };
    delete (data as FacilityAnalogGateway & { fxoPorts?: unknown }).fxoPorts;
    delete (data as FacilityAnalogGateway & { fxsPorts?: unknown }).fxsPorts;

    const promise =
      isEditing && data.id != null
        ? api.put<IStatusResponse>(`/facility-analog-gateway/${data.id}`, { gateway: data })
        : api.post<IStatusResponse>('/facility-analog-gateway', { gateway: data });

    void promise.then(
      (res) => {
        setSaving(false);
        if (!res.data?.success) {
          setError('An unexpected error occurred');
        } else {
          onClose(true);
        }
      },
      (err: { response?: { status?: number } }) => {
        setSaving(false);
        if (err?.response?.status === 400) {
          setError('Please verify the inputs are valid and try again');
        } else {
          setError('An unexpected error occurred');
        }
      }
    );
  };

  const handleClose = () => {
    if (!saving) {
      onClose(false);
    }
  };

  const stations = lookups.result?.stations ?? [];
  const facilityNumbers = lookups.result?.facilityNumbers ?? [];
  const lookupsLoading = lookups.loading && modalOpen;

  return (
    <Modal show={modalOpen} onHide={handleClose} size="lg" backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit' : 'Add'} Analog Gateway</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error != null && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          {lookupsLoading && (
            <p className="text-muted">Loading…</p>
          )}
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  required
                  value={gateway.name}
                  onChange={(e) => setGateway((g) => ({ ...g, name: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Model</Form.Label>
                <Form.Control
                  required
                  value={gateway.model}
                  onChange={(e) => setGateway((g) => ({ ...g, model: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>IP Address</Form.Label>
                <Form.Control
                  required
                  value={gateway.ipAddress}
                  onChange={(e) => setGateway((g) => ({ ...g, ipAddress: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>SIP Auth Username</Form.Label>
                <Form.Control
                  required
                  value={gateway.sipAuthUsername ?? ''}
                  onChange={(e) =>
                    setGateway((g) => ({ ...g, sipAuthUsername: e.target.value || null }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>SIP Auth Password</Form.Label>
                <Form.Control
                  required
                  type="password"
                  value={gateway.sipAuthPassword ?? ''}
                  onChange={(e) =>
                    setGateway((g) => ({ ...g, sipAuthPassword: e.target.value || null }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <h5>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="me-1"
                  onClick={() => addPort('FXO')}>
                  Add
                </Button>
                <Badge bg="dark" text="light" className="me-1">
                  {fxoPorts.length}
                </Badge>
                FXO Ports
              </h5>
              {fxoPorts.map((port) => {
                const idx = gateway.analogGatewayPorts.indexOf(port);
                return (
                  <AnalogGatewayPortRow
                    key={`fxo-${port.hardwarePort}-${port.name}-${idx}`}
                    port={port}
                    portIndex={idx}
                    stations={stations}
                    facilityNumbers={facilityNumbers}
                    updatePort={updatePort}
                    removePort={removePort}
                    togglePortStatus={togglePortStatus}
                  />
                );
              })}
            </Col>
            <Col md={12} className="mt-2">
              <h5>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="me-1"
                  onClick={() => addPort('FXS')}>
                  Add
                </Button>
                <Badge bg="primary" className="me-1">
                  {fxsPorts.length}
                </Badge>
                FXS Ports
              </h5>
              {fxsPorts.map((port) => {
                const idx = gateway.analogGatewayPorts.indexOf(port);
                return (
                  <AnalogGatewayPortRow
                    key={`fxs-${port.hardwarePort}-${port.name}-${idx}`}
                    port={port}
                    portIndex={idx}
                    stations={stations}
                    facilityNumbers={facilityNumbers}
                    updatePort={updatePort}
                    removePort={removePort}
                    togglePortStatus={togglePortStatus}
                  />
                );
              })}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving || lookupsLoading}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddEditModal;
